import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HttpAction } from '../studio-http-client/http-action.enum';
import { SchoolResponse } from '../models/school-response';
import { HttpContext, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { SchoolMetaDataResponse } from '../models/school-meta-data-response';
import { ClassResponse } from '../models/class-response';
import { MediaType } from '../enums/media-type.enum';
import { LibraryIdsResponse } from '../models/library-ids-response';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SchoolsService {
  apiService = inject(ApiService);

  getSchool(schoolId: string, context: HttpContext = null): Observable<SchoolResponse> {
    const getURL = `api/Schools/${schoolId}`;
    return this.apiService.invokeLSApi<SchoolResponse>(getURL, HttpAction.Get, null, {
      context,
      additionalHeaders: new HttpHeaders({ SchoolId: schoolId }),
    });
  }

  getSchoolsByUserId(userId: string, includeHasActiveClasses = false) {
    const getURL = `api/Users/${userId}/AdministeredSchools?includeHasActiveClasses=${includeHasActiveClasses}`;
    return this.apiService.invokeLSApi<SchoolResponse[]>(getURL, HttpAction.Get, null);
  }

  getSchoolMetaData(
    schoolId: string,
    sendSchoolId = false,
    context: HttpContext = null
  ): Observable<SchoolMetaDataResponse> {
    const getURL = `api/Schools/${schoolId}/MetaData`;
    return this.apiService.invokeLSApi<SchoolMetaDataResponse>(getURL, HttpAction.Get, null, {
      context,
      additionalHeaders: sendSchoolId ? new HttpHeaders({ SchoolId: schoolId }) : null,
    });
  }

  getClassesInSchool(schoolId: string, useSchoolIdHeader = false): Observable<ClassResponse[]> {
    const getURL = `api/Schools/${schoolId}/Classes`;
    return this.apiService.invokeLSApi<ClassResponse[]>(getURL, HttpAction.Get, null, {
      additionalHeaders: useSchoolIdHeader ? new HttpHeaders({ SchoolId: schoolId }) : null,
    });
  }

  getLibraryIds(
    schoolId: string,
    mediaTypes: MediaType[] = [MediaType.Book],
    lsSharedLibrariesOnly = false,
    isEncyclopedia = false,
    forMyStudentUserId: string = null,
    communityLibraries: boolean = null,
    userAuthoredLibraries: boolean = null,
    httpContext: HttpContext = null
  ): Observable<LibraryIdsResponse> {
    if (lsSharedLibrariesOnly) {
      const libraryIds = [];
      if (mediaTypes.includes(MediaType.Book)) {
        libraryIds.push('1aaeb8d4-8dda-11ea-8866-000af7d55b90');
      }
      if (mediaTypes.includes(MediaType.Video)) {
        libraryIds.push('b4fd8882-bf64-11ea-828c-000af7eac480');
      }
      if (mediaTypes.includes(MediaType.ActionClip)) {
        libraryIds.push('bbf6b208-bf64-11ea-828c-000af7eac480');
      }
      return of({ libraryIds });
    } else if (isEncyclopedia) {
      let getUrl = `api/Schools/${schoolId}/LibraryIds?mediaTypes=${[MediaType.Encyclopedia].join(',')}`;
      if (forMyStudentUserId) {
        getUrl += `&forMyStudentUserId=${forMyStudentUserId}`;
      }
      return this.apiService.invokeLSApi<LibraryIdsResponse>(getUrl, HttpAction.Get, null).pipe(
        map((res: LibraryIdsResponse) => {
          const libraryIds: string[] = [];
          if (
            mediaTypes.includes(MediaType.Encyclopedia) &&
            res.libraryIds.includes('13307afe-c98c-4964-44f5-08d8854b7c1b')
          ) {
            libraryIds.push('13307afe-c98c-4964-44f5-08d8854b7c1b');
          }

          if (
            mediaTypes.includes(MediaType.BackInTime) &&
            res.libraryIds.includes('30d96854-3549-11eb-9c2d-000af7eac7d0')
          ) {
            libraryIds.push('30d96854-3549-11eb-9c2d-000af7eac7d0');
          }

          if (
            mediaTypes.includes(MediaType.SpecialReport) &&
            res.libraryIds.includes('362270c6-3549-11eb-9c2d-000af7eac7d0')
          ) {
            libraryIds.push('362270c6-3549-11eb-9c2d-000af7eac7d0');
          }
          return { libraryIds };
        })
      );
    } else {
      const libraryIds: string[] = [];
      if (mediaTypes.includes(MediaType.HelpVideo)) {
        libraryIds.push('6c766f38-7a9f-11eb-9662-000af7eac480');
      }

      if (libraryIds.length > 0) {
        return of({ libraryIds });
      }

      let getUrl = `api/Schools/${schoolId}/LibraryIds?mediaTypes=${mediaTypes.join(',')}`;
      if (forMyStudentUserId) {
        getUrl += `&forMyStudentUserId=${forMyStudentUserId}`;
      }
      if (communityLibraries) {
        getUrl += `&communityLibraries=${communityLibraries}`;
      }
      if (userAuthoredLibraries === false && mediaTypes.includes(MediaType.Book)) {
        getUrl += `&userAuthoredLibraries=${userAuthoredLibraries}`;
      }

      return this.apiService.invokeLSApi<LibraryIdsResponse>(getUrl, HttpAction.Get, null, {
        context: httpContext,
      });
    }
  }
}
